import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productService, categoryService, carModelService, carBrandService } from '../services/api';
import ProductCard from '../components/ProductCard';
import Loading from '../components/Loading';

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [carBrands, setCarBrands] = useState([]);
  const [brandModels, setBrandModels] = useState({});
  const [allModels, setAllModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, brandsRes, modelsRes] = await Promise.all([
          productService.getAll(),
          categoryService.getAll(),
          carBrandService.getAll(),
          carModelService.getAll()
        ]);
        
        const productsData = productsRes?.data?.data || [];
        const categoriesData = categoriesRes?.data?.data || [];
        const brandsData = brandsRes?.data?.data || [];
        const modelsData = modelsRes?.data?.data || [];
        
        // Group models by brand
        const modelsByBrand = {};
        brandsData.forEach(brand => {
          const brandModels = modelsData.filter(model => model.brand_id === brand.id);
          if (brandModels.length > 0) {
            modelsByBrand[brand.id] = brandModels;
          }
        });
        
        // Filter brands that have models and sort by number of models (descending)
        const brandsWithModels = brandsData
          .filter(brand => modelsByBrand[brand.id] && modelsByBrand[brand.id].length > 0)
          .sort((a, b) => {
            const aCount = modelsByBrand[a.id].length;
            const bCount = modelsByBrand[b.id].length;
            return bCount - aCount;
          })
          .slice(0, 5); // Take top 5 brands
        
        setProducts(productsData.slice(0, 8));
        setCategories(categoriesData);
        setCarBrands(brandsWithModels);
        setBrandModels(modelsByBrand);
        setAllModels(modelsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setProducts([]);
        setCategories([]);
        setCarBrands([]);
        setBrandModels({});
        setAllModels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <Loading />;

  return (
    <div className="pb-2">
      {/* Car Brands with Models Section */}
      <section className="container mx-auto px-4 py-6 sm:py-12 w-full" dir="rtl">
        {carBrands.map(brand => (
          <div key={brand.id} className="mb-6 sm:mb-12">
            <h3 className="text-lg sm:text-2xl font-bold mb-3 sm:mb-6 text-right text-primary">
              {brand.name}
            </h3>
            
            {brandModels[brand.id]?.length > 0 ? (
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <div className="flex gap-3 sm:gap-4 pb-4">
                  {brandModels[brand.id].map(model => (
                    <Link
                      key={model.id}
                      to={`/products?model=${model.id}`}
                      className="flex-shrink-0 w-32 sm:w-48 bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
                    >
                      {model.image_url ? (
                        <div className="h-32 sm:h-48 overflow-hidden">
                          <img
                            src={model.image_url.startsWith('http') ? model.image_url : `https://api.iraq-parts.com${model.image_url}`}
                            alt={model.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                      ) : (
                        <div className="h-32 sm:h-48 bg-gray-200 flex items-center justify-center">
                          <span className="text-2xl sm:text-4xl">🚗</span>
                        </div>
                      )}
                      <div className="p-2 sm:p-3 text-center">
                        <h4 className="font-semibold text-gray-800 text-xs sm:text-sm line-clamp-2">
                          {model.name}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-right text-sm sm:text-base">لا توجد موديلات لهذه الماركة</p>
            )}
          </div>
        ))}
      </section>

      {/* Categories Section */}
      <section className="container mx-auto px-4 py-6 sm:py-12 w-full">
        <h2 className="text-xl sm:text-3xl font-bold mb-4 sm:mb-8 text-right">تسوق حسب القسم</h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
          {categories.map(category => (
            <Link
              key={category.id}
              to={`/products?category=${category.id}`}
              className="bg-white p-3 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center group"
            >
              <div className="mb-2 sm:mb-3">
                <span className="text-2xl sm:text-3xl">📦</span>
              </div>
              <h3 className="font-semibold text-sm sm:text-lg text-gray-800 line-clamp-2">
                {category.name}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2">
                {category.product_count} منتج
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="container mx-auto px-4 py-6 sm:py-12">
        <div className="flex flex-row-reverse justify-between items-center mb-4 sm:mb-8">
          <h2 className="text-xl sm:text-3xl font-bold">المنتجات المميزة</h2>
          <Link to="/products" className="text-sm sm:text-base text-primary hover:underline font-semibold">
            عرض الكل ←
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
          {products.map(product => (
            <ProductCard key={product.id} product={product} allModels={allModels} />
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50 py-6 sm:py-12 w-full">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8">
            <div className="text-center bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl sm:text-4xl mb-2 sm:mb-4">🚚</div>
              <h3 className="text-base sm:text-xl font-semibold mb-1 sm:mb-2">شحن سريع</h3>
              <p className="text-xs sm:text-base text-gray-600">
                توصيل قطع الغيار بسرعة إلى باب منزلك
              </p>
            </div>
            <div className="text-center bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl sm:text-4xl mb-2 sm:mb-4">✅</div>
              <h3 className="text-base sm:text-xl font-semibold mb-1 sm:mb-2">جودة مضمونة</h3>
              <p className="text-xs sm:text-base text-gray-600">
                جميع القطع مختبرة ومعتمدة للجودة
              </p>
            </div>
            <div className="text-center bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl sm:text-4xl mb-2 sm:mb-4">💰</div>
              <h3 className="text-base sm:text-xl font-semibold mb-1 sm:mb-2">أفضل الأسعار</h3>
              <p className="text-xs sm:text-base text-gray-600">
                أسعار منافسة على جميع قطع السيارات
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
